import React, { useMemo } from 'react'
import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useClient } from '@/stores/useClient.ts'
import { useNavigate, useParams } from 'react-router-dom'
import { DriveViewContext, DriveViewAlbum } from '@/contexts/driveViewContext.tsx'
import { DriveViewFilesContext } from '@/contexts/driveViewFilesContext.tsx'
import { AlbumFile } from '@/models/albumFile.ts'
import { getURLParamsForFilter, useFileFilter } from '@/contexts/fileFilter.tsx'
import { resolveTheme } from '@/models/drive.ts'

export function ShareDriveViewProvider({ children }: { children: React.ReactNode }) {
  const { getShare, getShareAlbums, getShareAlbumFiles } = useClient()
  const { shareId, shareAlbumId } = useParams<{ shareId: string, shareAlbumId?: string }>()
  const navigate = useNavigate()

  const shareQuery = useQuery({
    queryKey: ['share', shareId],
    queryFn: async () => {
      if (!shareId) return null
      const res = await getShare(shareId)
      return res.data
    },
    enabled: !!shareId,
  })

  const albumsQuery = useQuery({
    queryKey: ['share', shareId, 'albums'],
    queryFn: async () => {
      if (!shareId) return []
      const res = await getShareAlbums(shareId)
      return res.data
    },
    enabled: !!shareId,
  })

  const albums: DriveViewAlbum[] = useMemo(() => {
    return (albumsQuery.data || []).map(a => ({
      id: a.id,
      album_id: a.album_id,
      title: a.title,
      description: a.description,
      thumbnail: a.thumbnail,
      files_count: a.files_count,
      preview_desktop: a.preview_desktop,
      preview_mobile: a.preview_mobile,
      preview_desktop_center_x: a.preview_desktop_center_x,
      preview_desktop_center_y: a.preview_desktop_center_y,
      preview_mobile_center_x: a.preview_mobile_center_x,
      preview_mobile_center_y: a.preview_mobile_center_y,
    }))
  }, [albumsQuery.data])

  const currentAlbum = useMemo(() => {
    if (!albums.length) return null
    if (shareAlbumId) {
      return albums.find(a => a.id === shareAlbumId) || albums[0]
    }
    return albums[0]
  }, [albums, shareAlbumId])

  function onSelectAlbum(album: DriveViewAlbum) {
    navigate(`/s/${shareId}/${album.id}`)
  }

  async function fetchAlbumFiles(albumId: string) {
    const res = await getShareAlbumFiles(shareId!, albumId)
    return res.data
  }

  const metadata = useMemo(() => {
    if (!shareQuery.data) return null
    return {
      title: shareQuery.data.title,
      driveTitle: shareQuery.data.drive_title,
      driveType: shareQuery.data.drive_type,
      theme: resolveTheme(shareQuery.data.theme_name, shareQuery.data.drive_type),
      spacing: shareQuery.data.spacing,
      layoutMode: shareQuery.data.layout_mode,
      albumOverview: shareQuery.data.album_overview,
      albumOverviewStyle: shareQuery.data.album_overview_style,
      areElementsContained: shareQuery.data.are_elements_contained,
      contact: shareQuery.data.contact,
      userDefinedExpiresAt: shareQuery.data.user_defined_expires_at,
      openedImageCaption: shareQuery.data.opened_image_caption,
      downloadOptions: shareQuery.data.download_options,
    }
  }, [shareQuery.data])

  return (
    <DriveViewContext.Provider value={{
      metadata,
      albums,
      currentAlbum,
      isLoading: shareQuery.isLoading || albumsQuery.isLoading,
      error: (shareQuery.error || albumsQuery.error) as Error | null,
      onSelectAlbum,
      fetchAlbumFiles,
    }}>
      {currentAlbum ? (
        <ShareDriveViewFilesProvider shareId={shareId!} shareAlbumId={currentAlbum.id}>
          {children}
        </ShareDriveViewFilesProvider>
      ) : (
        <DriveViewFilesContext.Provider value={null}>
          {children}
        </DriveViewFilesContext.Provider>
      )}
    </DriveViewContext.Provider>
  )
}

function ShareDriveViewFilesProvider({ shareId, shareAlbumId, children }: { shareId: string, shareAlbumId: string, children: React.ReactNode }) {
  const { getShareAlbumFiles } = useClient()
  const { fileFilter } = useFileFilter()

  const filterParams = useMemo(() => {
    if (!fileFilter) return undefined
    const params = getURLParamsForFilter(fileFilter)
    if (params.length === 0) return undefined
    return Object.fromEntries(params)
  }, [fileFilter])

  const query = useInfiniteQuery({
    enabled: !!shareId && !!shareAlbumId,
    queryKey: ['share', shareId, 'album', shareAlbumId, 'files', filterParams],
    initialPageParam: null as string | null,
    async queryFn({ pageParam }) {
      if (pageParam) {
        const response = await fetch(pageParam)
        return response.json()
      }
      const res = await getShareAlbumFiles(shareId, shareAlbumId, filterParams)
      return res.data
    },
    getNextPageParam(page: any) {
      return page.next
    },
  })

  const fetchedFiles = useMemo(() => {
    if (!query.data) return []
    const files: Array<AlbumFile> = [
      // @ts-ignore
      ...(query.data.pages.flatMap((page: any) => page.results || page))
    ]
    return files
  }, [query.data])

  return (
    <DriveViewFilesContext.Provider value={{
      currentFilesCollection: { files: fetchedFiles },
      hasNextPage: query.hasNextPage,
      fetchNextPage: query.fetchNextPage,
      isLoading: query.isLoading,
    }}>
      {children}
    </DriveViewFilesContext.Provider>
  )
}
